import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { Spinner } from '@incubs/ui';
import AppShell from './components/layout/AppShell';

// Lazy-loaded pages
const Landing = React.lazy(() => import('./pages/Landing'));
const SignIn = React.lazy(() => import('./pages/auth/SignIn'));
const SignUp = React.lazy(() => import('./pages/auth/SignUp'));
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage'));
const EditProfile = React.lazy(() => import('./pages/profile/EditProfile'));
const DiscoverPage = React.lazy(() => import('./pages/discover/DiscoverPage'));
const ChatPage = React.lazy(() => import('./pages/chat/ChatPage'));
const VideoPage = React.lazy(() => import('./pages/video/VideoPage'));
const StartupList = React.lazy(() => import('./pages/startups/StartupList'));
const StartupDetail = React.lazy(() => import('./pages/startups/StartupDetail'));
const SubmitStartup = React.lazy(() => import('./pages/startups/SubmitStartup'));
const MatchesPage = React.lazy(() => import('./pages/matches/MatchesPage'));
const AdminDashboard       = React.lazy(() => import('./pages/admin/AdminDashboard'));
const TimelinePage         = React.lazy(() => import('./pages/timeline'));
const IncubatorStatusPage  = React.lazy(() => import('./pages/incubator/IncubatorStatusPage'));
const ApplyPage            = React.lazy(() => import('./pages/apply/ApplyPage'));

function LoadingFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <Spinner size="lg" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <LoadingFallback />;
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;
  return <>{children}</>;
}

export function AppRouter() {
  return (
    <React.Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/timeline" element={<TimelinePage />} />
        <Route path="/apply" element={<ApplyPage />} />
        <Route path="/sign-in/*" element={<SignIn />} />
        <Route path="/sign-up/*" element={<SignUp />} />

        {/* Protected */}
        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/edit" element={<EditProfile />} />
          <Route path="/profile/:userId" element={<ProfilePage />} />
          <Route path="/matches" element={<MatchesPage />} />
          <Route path="/discover" element={<DiscoverPage />} />
          <Route path="/chat" element={<ChatPage />} />
          <Route path="/chat/:conversationSid" element={<ChatPage />} />
          <Route path="/video/:roomName" element={<VideoPage />} />
          <Route path="/startups" element={<StartupList />} />
          <Route path="/startups/new" element={<SubmitStartup />} />
          <Route path="/startups/:id" element={<StartupDetail />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/incubator" element={<IncubatorStatusPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
}
